import { Component } from '@theme/component';
import { debounce, getSearchDropdownAnchorBottom, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds predictive-search section rendering URL (local copy avoids import failures).
 * @param {string} sectionId
 * @param {URL} url
 * @returns {string}
 */
function buildPredictiveSearchSectionUrl(sectionId, url) {
  const sectionUrl = new URL(url.href);
  const normalizedId = sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
  sectionUrl.searchParams.set('section_id', normalizedId);
  sectionUrl.searchParams.sort();
  return sectionUrl.toString();
}

/**
 * @param {HTMLElement | null | undefined} searchRoot
 */
function applySearchDropdownTopCSSProperties(searchRoot) {
  const headerRow = document.querySelector('#header-component .header__row--top');
  const anchorBottom = getSearchDropdownAnchorBottom(searchRoot);
  if (!(headerRow instanceof HTMLElement) || anchorBottom == null) return;

  const containingBlock =
    headerRow.closest('.header-section') ||
    headerRow.closest('#header-component') ||
    document.documentElement;

  const containerRect = containingBlock.getBoundingClientRect();
  const top = Math.max(0, Math.round(anchorBottom - containerRect.top));
  const viewportTop = Math.max(0, Math.round(anchorBottom));

  document.body.style.setProperty('--search-dropdown-top', `${top}px`);
  document.body.style.setProperty('--search-dropdown-viewport-top', `${viewportTop}px`);
}

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} [resetButton] - The reset button element (not used in header dropdown).
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  /** predictiveSearchResults lives inside a panel portaled to document.body in dropdown mode. */
  requiredRefs = ['searchInput'];

  /** @returns {HTMLInputElement | null} */
  #getSearchInput() {
    const { searchInput } = this.refs;
    if (searchInput instanceof HTMLInputElement) return searchInput;

    const input = this.querySelector('[ref="searchInput"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** @type {AbortController | null} */
  #searchFetch = null;

  /** @type {number} */
  #searchRequestId = 0;

  #emptyStateLoaded = false;

  /** @type {HTMLElement | null} */
  #cachedEmptyResults = null;

  /** @type {ResizeObserver | null} */
  #promoResizeObserver = null;

  /** @type {number | null} */
  #scrollSyncFrame = null;

  /** Ignore the click that opened the dropdown (focus/click fire in the same gesture). */
  #ignoreOutsidePointerUntil = 0;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  get #isDropdownMode() {
    return this.dataset.searchMode === 'dropdown';
  }

  get #searchAction() {
    return this.closest('.search-action');
  }

  /** @returns {boolean} */
  #isDropdownFullyOpen() {
    if (!this.#isDropdownMode) return false;
    if (!this.#searchAction?.classList.contains('is-open')) return false;
    if (!document.body.classList.contains('search-dropdown-open')) return false;

    const panel = this.#getDropdownPanelCandidates().find((candidate) => candidate.isConnected);
    return panel?.classList.contains('predictive-search-dropdown-panel--open') ?? false;
  }

  /** @returns {boolean} */
  #shouldCloseDropdown() {
    if (!this.#isDropdownMode) return false;

    if (
      this.#searchAction?.classList.contains('is-open') ||
      document.body.classList.contains('search-dropdown-open')
    ) {
      return true;
    }

    return this.#getDropdownPanelCandidates().some((panel) =>
      panel.classList.contains('predictive-search-dropdown-panel--open')
    );
  }

  /** @returns {boolean} */
  #isDropdownOpen() {
    return this.#isDropdownFullyOpen();
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getDropdownPanelCandidates() {
    const testId = this.dataset.testid;
    if (!testId) return [];

    return Array.from(
      document.querySelectorAll(
        `.predictive-search-form__content-wrapper[data-search-component="${CSS.escape(testId)}"]`
      )
    ).filter((node) => node instanceof HTMLElement);
  }

  /**
   * Keep a single panel node; remove stale copies left on document.body after re-renders.
   * @returns {HTMLElement | null}
   */
  #dedupeDropdownPanels() {
    const panels = this.#getDropdownPanelCandidates();
    if (panels.length <= 1) return panels[0] ?? null;

    const form = this.querySelector('.predictive-search-form');
    const refPanel = this.#getResultsMount()?.closest('.predictive-search-form__content-wrapper');

    const preferred =
      panels.find((panel) => panel.classList.contains('predictive-search-dropdown-panel--open')) ??
      panels.find((panel) => panel.dataset.portaled === 'true') ??
      (refPanel instanceof HTMLElement && panels.includes(refPanel) && refPanel.isConnected ? refPanel : null) ??
      panels.find((panel) => this.contains(panel) || form?.contains(panel)) ??
      panels[panels.length - 1];

    for (const panel of panels) {
      if (panel === preferred) continue;
      panel.remove();
    }

    return preferred;
  }

  /**
   * @param {HTMLElement} wrapper
   */
  #clearDropdownPanelInlineStyles(wrapper) {
    const inner = wrapper.querySelector('.predictive-search-form__content-inner');
    const promo = wrapper.querySelector('.predictive-search-form__promo-panel');

    wrapper.classList.remove('predictive-search-dropdown-panel--open');
    wrapper.style.removeProperty('top');
    wrapper.style.removeProperty('margin-top');
    wrapper.style.removeProperty('left');
    wrapper.style.removeProperty('width');
    wrapper.style.removeProperty('right');
    wrapper.style.removeProperty('z-index');
    wrapper.style.removeProperty('pointer-events');
    wrapper.style.removeProperty('background-color');

    if (inner instanceof HTMLElement) {
      inner.style.removeProperty('width');
      inner.style.removeProperty('max-width');
      inner.style.removeProperty('margin-left');
      inner.style.removeProperty('margin-right');
      inner.style.removeProperty('padding-inline');
      inner.style.removeProperty('--search-promo-top');
      inner.style.removeProperty('--search-promo-height');
    }

    if (promo instanceof HTMLElement) {
      promo.classList.remove('is-sized');
      promo.style.removeProperty('top');
      promo.style.removeProperty('height');
      promo.style.removeProperty('max-height');
    }
  }

  #handleDropdownEscape = (event) => {
    if (!this.#isDropdownOpen()) return;

    event.preventDefault();
    this.#closeDropdown();
    this.#restoreEmptyState();
  };

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    const searchInput = this.#getSearchInput();
    if (searchInput?.value.length > 0) {
      this.#showResetButton();
    }

    if (searchInput instanceof HTMLInputElement && !this.#isDropdownMode) {
      searchInput.addEventListener('input', this.#onSearchInput, { signal });
      searchInput.addEventListener('keyup', this.#onSearchInput, { signal });
    }

    if (this.#isDropdownMode) {
      this.#dedupeDropdownPanels();
      this.#restoreDropdownPanelInForm();
      this.#setupDropdown({ signal });
      requestAnimationFrame(() => {
        this.#dedupeDropdownPanels();
        this.#restoreDropdownPanelInForm();
        this.#cacheDropdownEmptyState();
      });
    } else if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (!this.#isDropdownMode && RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  updatedCallback() {
    super.updatedCallback();

    if (!this.#isDropdownMode) return;

    if (this.#isDropdownFullyOpen()) {
      this.#dedupeDropdownPanels();
      this.#portalDropdownPanelToBody();
      this.#getDropdownPanel()?.classList.add('predictive-search-dropdown-panel--open');
      this.#updateSearchDropdownPosition();
      return;
    }

    this.#dedupeDropdownPanels();
  }

  /**
   * @param {{ signal: AbortSignal }} options
   */
  #setupDropdown({ signal }) {
    document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        this.#handleDropdownEscape(event);
      },
      { signal, capture: true }
    );

    document.addEventListener(
      'mousedown',
      (event) => {
        if (performance.now() < this.#ignoreOutsidePointerUntil) return;
        if (!this.#shouldCloseDropdown()) return;
        if (this.#isEventInsideDropdown(event)) return;
        this.#closeDropdown();
      },
      { signal }
    );

    const dropdownSearchInput = this.#getSearchInput();
    if (dropdownSearchInput instanceof HTMLInputElement) {
      dropdownSearchInput.addEventListener(
        'pointerdown',
        () => {
          if (!this.#isDropdownFullyOpen()) {
            this.openDropdown();
          }
        },
        { signal }
      );
      dropdownSearchInput.addEventListener('input', this.#onSearchInput, { signal });
    }

    document.addEventListener(
      'click',
      (event) => {
        if (!this.#isDropdownOpen()) return;

        if (this.#isEventInsideDropdown(event)) {
          const target = event.target;
          if (target instanceof Node) {
            for (const panel of this.#getDropdownPanelCandidates()) {
              if (!panel.contains(target)) continue;
              const link =
                target instanceof Element ? target.closest('a[href]') : null;
              if (link instanceof HTMLAnchorElement && link.href && link.href !== '#') {
                event.stopPropagation();
              }
              break;
            }
          }
        }
      },
      { signal }
    );

    window.addEventListener('scroll', this.#syncSearchDropdownPosition, { signal, passive: true });
    window.addEventListener('resize', this.#syncSearchDropdownPosition, { signal });
    document.addEventListener('scroll', this.#syncSearchDropdownPosition, { signal, passive: true, capture: true });
    document.addEventListener('search-dropdown:reposition', this.#syncSearchDropdownPosition, { signal });

    const headerComponent = document.getElementById('header-component');
    if (headerComponent instanceof HTMLElement) {
      const headerLayoutObserver = new MutationObserver(() => this.#syncSearchDropdownPosition());
      headerLayoutObserver.observe(headerComponent, {
        attributes: true,
        attributeFilter: ['data-sticky-state', 'data-scroll-direction'],
      });
      signal.addEventListener('abort', () => headerLayoutObserver.disconnect(), { once: true });
    }

    const announcement = document.querySelector('.announcement-bar');
    if (announcement instanceof HTMLElement) {
      const syncOnAnnouncementLayoutChange = () => this.#syncSearchDropdownPosition();
      const announcementResizeObserver = new ResizeObserver(syncOnAnnouncementLayoutChange);
      announcementResizeObserver.observe(announcement);
      signal.addEventListener('abort', () => announcementResizeObserver.disconnect(), { once: true });
    }

    if (window.visualViewport) {
      window.visualViewport.addEventListener('scroll', this.#syncSearchDropdownPosition, { signal, passive: true });
      window.visualViewport.addEventListener('resize', this.#syncSearchDropdownPosition, { signal });
    }

    document.addEventListener(
      ThemeEvents.megaMenuHover,
      () => {
        if (!this.#searchAction?.classList.contains('is-open')) return;
        this.#closeDropdown();
        this.#getSearchInput()?.blur();
      },
      { signal }
    );

    this.addEventListener('click', this.#handleModalClick, { signal });
  }

  /**
   * @param {Event} event
   */
  #onSearchInput = (event) => {
    if (event instanceof InputEvent && event.isComposing) return;

    if (this.#isDropdownMode) {
      this.#runPredictiveSearch();
      return;
    }

    this.#runPredictiveSearchDebounced(event);
  };

  /** @returns {boolean} */
  #isSearchInFlight() {
    return !!(this.#searchFetch && !this.#searchFetch.signal.aborted);
  }

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #isEventInsideDropdown(event) {
    const panels = this.#getDropdownPanelCandidates();
    const path = event.composedPath?.() ?? [event.target];

    for (const node of path) {
      if (!(node instanceof Node)) continue;

      if (node instanceof Element && node.matches('[data-search-dropdown-backdrop]')) {
        return false;
      }

      if (this.#searchAction?.contains(node)) return true;
      if (this.contains(node)) return true;

      for (const panel of panels) {
        if (panel.contains(node)) return true;
      }
    }

    return false;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getDropdownPanel() {
    return this.#dedupeDropdownPanels();
  }

  /**
   * Header uses contain:layout which traps position:fixed descendants; release it while search is open.
   * @param {boolean} open
   */
  #setHeaderContainmentForDropdown(open) {
    const header = document.getElementById('header-component');
    if (!(header instanceof HTMLElement)) return;

    if (open) {
      header.style.setProperty('contain', 'none');
    } else {
      header.style.removeProperty('contain');
    }
  }

  /** Move dropdown panel to body so fixed positioning and clicks are not trapped by the header. */
  #portalDropdownPanelToBody() {
    const panel = this.#dedupeDropdownPanels();
    if (!(panel instanceof HTMLElement)) return;
    if (panel.dataset.portaled === 'true' && panel.parentElement === document.body) return;

    const backdrop = document.querySelector('[data-search-dropdown-backdrop]');
    if (backdrop instanceof HTMLElement) {
      document.body.insertBefore(panel, backdrop);
    } else {
      document.body.appendChild(panel);
    }

    panel.dataset.portaled = 'true';
    panel.style.setProperty('background-color', '#f4f4f4', 'important');
  }

  /** Re-insert panel into the form if a previous session left it on document.body */
  #restoreDropdownPanelInForm() {
    const form = this.querySelector('.predictive-search-form');
    const testId = this.dataset.testid;
    if (!(form instanceof HTMLElement) || !testId) return;

    const panel = this.#dedupeDropdownPanels();
    if (!(panel instanceof HTMLElement)) return;

    if (form.contains(panel)) {
      delete panel.dataset.portaled;
      return;
    }

    const header = form.querySelector('.predictive-search-form__header');
    if (header?.parentNode === form && header.nextSibling) {
      form.insertBefore(panel, header.nextSibling);
    } else {
      form.appendChild(panel);
    }

    delete panel.dataset.portaled;
  }

  /**
   * @param {boolean} visible
   */
  #setSearchDropdownBackdropVisible(visible) {
    const backdrop = document.querySelector('[data-search-dropdown-backdrop]');
    if (!(backdrop instanceof HTMLElement)) return;

    if (visible) {
      backdrop.removeAttribute('hidden');
    } else {
      backdrop.setAttribute('hidden', '');
    }
  }

  #resetHeaderSubmenuLayout() {
    const headerMenu = document.querySelector('header-menu');
    const headerComponent = document.getElementById('header-component');

    if (headerMenu?.getAttribute('aria-expanded') === 'true') return;

    headerComponent?.style.setProperty('--submenu-height', '0px');
    headerComponent?.style.setProperty('--full-open-header-height', '0px');
  }

  /**
   * Layout for the fixed dropdown panel. Anchor flush under the search input.
   */
  #getSearchDropdownLayout() {
    const headerRow =
      this.#searchAction?.closest('.header__row--top') ||
      document.querySelector('#header-component .header__row--top');

    if (!(headerRow instanceof HTMLElement)) return null;

    const anchorBottom = getSearchDropdownAnchorBottom(this);
    if (anchorBottom == null) return null;

    const containingBlock =
      headerRow.closest('.header-section') ||
      headerRow.closest('#header-component') ||
      document.documentElement;

    const columns = headerRow.querySelector('.header__columns') ?? headerRow;

    const containerRect = containingBlock.getBoundingClientRect();
    const columnsRect = columns.getBoundingClientRect();
    const top = Math.max(0, Math.round(anchorBottom - containerRect.top));
    const viewportTop = Math.max(0, Math.round(anchorBottom));
    const fullBleedLeft = Math.round(-containerRect.left);
    const innerInsetLeft = Math.max(0, Math.round(columnsRect.left));
    const innerWidth = Math.max(0, Math.round(columnsRect.width));

    return {
      relativeTop: `${top}px`,
      viewportTop: `${viewportTop}px`,
      fullBleedLeft: `${fullBleedLeft}px`,
      innerInsetLeft: `${innerInsetLeft}px`,
      innerWidth: `${innerWidth}px`,
    };
  }

  /**
   * @param {{ syncPromo?: boolean }} [options]
   */
  #updateSearchDropdownPosition({ syncPromo = true } = {}) {
    if (!this.#isDropdownMode) return;

    const layout = this.#getSearchDropdownLayout();
    if (!layout) return;

    const wrapper = this.#getActiveDropdownPanel();
    const inner = wrapper?.querySelector('.predictive-search-form__content-inner');

    applySearchDropdownTopCSSProperties(this);
    this.style.setProperty('--search-panel-top', layout.viewportTop);

    if (wrapper instanceof HTMLElement) {
      wrapper.style.setProperty('top', layout.viewportTop, 'important');
      wrapper.style.setProperty('transform', 'none', 'important');
      wrapper.style.setProperty('margin-top', '0', 'important');
      wrapper.style.setProperty('left', '0', 'important');
      wrapper.style.setProperty('right', '0', 'important');
      wrapper.style.setProperty('width', '100%', 'important');
      wrapper.style.setProperty('z-index', 'calc(var(--layer-overlay) + 2)', 'important');
      wrapper.style.setProperty('pointer-events', 'auto', 'important');
    }

    const backdrop = document.querySelector('[data-search-dropdown-backdrop]');
    if (backdrop instanceof HTMLElement) {
      backdrop.style.setProperty('top', layout.viewportTop, 'important');
    }

    if (inner instanceof HTMLElement) {
      inner.style.setProperty('width', layout.innerWidth, 'important');
      inner.style.setProperty('max-width', `${layout.innerWidth}px`, 'important');
      inner.style.setProperty('margin-left', layout.innerInsetLeft, 'important');
      inner.style.setProperty('margin-right', 'auto', 'important');
      inner.style.setProperty('padding-inline', '0', 'important');
    }

    if (syncPromo) {
      this.#syncPromoPanelLayout();
    }
  }

  /** @returns {HTMLElement | null} */
  #getResultsContentWrapper() {
    if (this.#isDropdownMode) {
      return this.#getActiveDropdownPanel();
    }

    const wrapper = this.querySelector('.predictive-search-form__content-wrapper');
    return wrapper instanceof HTMLElement ? wrapper : null;
  }

  /** Toggle promo panel vs query product grid (dropdown + mobile modal). */
  #syncSearchResultsLayout() {
    const panel = this.#getResultsContentWrapper();
    const promo = panel?.querySelector('.predictive-search-form__promo-panel');
    const isQuery = this.#isShowingQueryResults();

    panel?.classList.toggle('predictive-search-form__content-wrapper--query-results', isQuery);

    if (isQuery && promo instanceof HTMLElement) {
      promo.classList.remove('is-sized');
    }
  }

  #syncPromoPanelLayout() {
    if (!this.#isDropdownMode) return;
    if (!this.#searchAction?.classList.contains('is-open')) return;

    this.#syncSearchResultsLayout();

    if (this.#isShowingQueryResults()) return;

    const panel = this.#getDropdownPanel();
    const inner = panel?.querySelector('.predictive-search-form__content-inner');
    const promo = panel?.querySelector('.predictive-search-form__promo-panel');
    const resultsArea = panel?.querySelector('.predictive-search-form__results-area');

    if (!(inner instanceof HTMLElement) || !(promo instanceof HTMLElement) || !(resultsArea instanceof HTMLElement)) {
      return;
    }

    const innerRect = inner.getBoundingClientRect();
    const areaRect = resultsArea.getBoundingClientRect();
    const top = Math.round(areaRect.top - innerRect.top);
    const height = Math.round(areaRect.height);

    if (height <= 0) {
      promo.classList.remove('is-sized');
      return;
    }

    inner.style.setProperty('--search-promo-top', `${top}px`);
    inner.style.setProperty('--search-promo-height', `${height}px`);
    promo.style.setProperty('top', `${top}px`, 'important');
    promo.style.setProperty('height', `${height}px`, 'important');
    promo.style.setProperty('max-height', `${height}px`, 'important');
    promo.classList.add('is-sized');
  }

  #schedulePromoPanelLayout() {
    if (!this.#isDropdownMode) return;

    this.#syncPromoPanelLayout();
    requestAnimationFrame(() => {
      this.#syncPromoPanelLayout();
      requestAnimationFrame(() => this.#syncPromoPanelLayout());
    });
  }

  #observePromoLayoutTargets() {
    this.#disconnectPromoResizeObserver();
    if (!this.#isDropdownMode) return;

    const panel = this.#getDropdownPanel();
    const resultsArea = panel?.querySelector('.predictive-search-form__results-area');
    const grid = panel?.querySelector('.predictive-search-results__wrapper-products');
    if (!(resultsArea instanceof HTMLElement)) return;

    this.#schedulePromoPanelLayout();

    grid?.querySelectorAll('img').forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      if (img.complete) return;
      img.addEventListener('load', () => this.#schedulePromoPanelLayout(), { once: true });
    });

    const promoImg = panel?.querySelector('.predictive-search-form__promo-img');
    if (promoImg instanceof HTMLImageElement && !promoImg.complete) {
      promoImg.addEventListener('load', () => this.#schedulePromoPanelLayout(), { once: true });
    }

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.#schedulePromoPanelLayout());
    }

    this.#promoResizeObserver = new ResizeObserver(() => {
      if (!this.#searchAction?.classList.contains('is-open')) return;
      this.#syncPromoPanelLayout();
    });
    this.#promoResizeObserver.observe(resultsArea);
  }

  #disconnectPromoResizeObserver() {
    this.#promoResizeObserver?.disconnect();
    this.#promoResizeObserver = null;
  }

  #syncSearchDropdownPosition = () => {
    if (!this.#searchAction?.classList.contains('is-open')) return;
    this.#updateSearchDropdownPosition({ syncPromo: false });
  };

  /** Keep the portaled panel glued to the sticky header for every paint while open. */
  #startScrollPositionSync() {
    this.#stopScrollPositionSync();

    const tick = () => {
      if (!this.#searchAction?.classList.contains('is-open')) {
        this.#scrollSyncFrame = null;
        return;
      }

      this.#updateSearchDropdownPosition({ syncPromo: false });
      this.#scrollSyncFrame = requestAnimationFrame(tick);
    };

    this.#updateSearchDropdownPosition({ syncPromo: false });
    this.#scrollSyncFrame = requestAnimationFrame(tick);
  }

  #stopScrollPositionSync() {
    if (this.#scrollSyncFrame != null) {
      cancelAnimationFrame(this.#scrollSyncFrame);
      this.#scrollSyncFrame = null;
    }
  }

  openDropdown = () => {
    if (!this.#isDropdownMode) return;

    const searchInput = this.#getSearchInput();
    if (!(searchInput instanceof HTMLInputElement)) return;

    if (this.#isDropdownFullyOpen()) {
      this.#updateSearchDropdownPosition();
      this.#schedulePromoPanelLayout();
      this.#startScrollPositionSync();
      const searchTerm = searchInput.value.trim();
      if (searchTerm.length) {
        this.#getSearchResults(searchTerm);
      }
      return;
    }

    this.#ignoreOutsidePointerUntil = performance.now() + 400;

    this.#dedupeDropdownPanels();
    this.#restoreDropdownPanelInForm();
    this.#setHeaderContainmentForDropdown(true);
    this.#portalDropdownPanelToBody();

    const panel = this.#getDropdownPanel();
    panel?.classList.add('predictive-search-dropdown-panel--open');

    this.#searchAction?.classList.add('is-open');
    this.classList.add('is-open');
    document.body.classList.add('search-dropdown-open');
    this.#setSearchDropdownBackdropVisible(true);
    this.#updateSearchDropdownPosition();
    this.#schedulePromoPanelLayout();

    const searchTerm = searchInput.value.trim();

    if (!searchTerm.length) {
      this.#ensureEmptyStateVisible();
    } else {
      this.#showResetButton();
      this.#getSearchResults(searchTerm);
    }

    requestAnimationFrame(() => {
      this.#updateSearchDropdownPosition();
      this.#observePromoLayoutTargets();

      if (!searchInput.value.trim() && !this.#isSearchInFlight()) {
        this.#ensureEmptyStateVisible();
      }

      this.#schedulePromoPanelLayout();
    });
    searchInput.setAttribute('aria-expanded', 'true');

    if (document.activeElement !== searchInput) {
      searchInput.focus();
    }

    if (!this.#emptyStateLoaded && !searchTerm.length) {
      if (this.#hasSsrEmptyState()) {
        this.#emptyStateLoaded = true;
        this.#cacheDropdownEmptyState();
      } else {
        this.#loadEmptyState();
      }
    }

    this.#startScrollPositionSync();
  };

  /**
   * @param {string} searchTerm
   * @returns {URL}
   */
  #buildPredictiveSearchRequestUrl(searchTerm) {
    const base =
      typeof Theme !== 'undefined' && Theme.routes?.predictive_search_url
        ? Theme.routes.predictive_search_url
        : '/search/suggest';
    const url = new URL(base, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[limit]', '5');
    url.searchParams.set('resources[type]', 'product,query');
    return url;
  }

  /**
   * @param {string} html
   * @returns {HTMLElement | null}
   */
  #parsePredictiveSearchMarkup(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (
      doc.getElementById('predictive-search-results') ??
      doc.querySelector('#shopify-section-predictive-search #predictive-search-results') ??
      doc.querySelector('[id^="shopify-section-"] #predictive-search-results') ??
      doc.querySelector('.predictive-search-empty-section #predictive-search-results') ??
      doc.querySelector('.shopify-section #predictive-search-results')
    );
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getActiveDropdownPanel() {
    const panels = this.#getDropdownPanelCandidates();
    if (panels.length === 0) return null;
    if (panels.length === 1) return panels[0];

    const form = this.querySelector('.predictive-search-form');

    return (
      panels.find(
        (panel) =>
          panel.isConnected && panel.classList.contains('predictive-search-dropdown-panel--open')
      ) ??
      panels.find((panel) => panel.isConnected && panel.dataset.portaled === 'true') ??
      panels.find((panel) => panel.isConnected && (this.contains(panel) || form?.contains(panel))) ??
      panels.find((panel) => panel.isConnected) ??
      panels[panels.length - 1]
    );
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getResultsMount() {
    if (this.#isDropdownMode) {
      const openPanel = this.#getActiveDropdownPanel();
      const openMount = openPanel?.querySelector('[ref="predictiveSearchResults"]');
      if (openMount instanceof HTMLElement && openMount.isConnected) {
        return openMount;
      }

      const panel = this.#getDropdownPanel();
      const panelMount = panel?.querySelector('[ref="predictiveSearchResults"]');
      if (panelMount instanceof HTMLElement && panelMount.isConnected) {
        return panelMount;
      }
    }

    const { predictiveSearchResults } = this.refs;
    if (predictiveSearchResults instanceof HTMLElement && predictiveSearchResults.isConnected) {
      return predictiveSearchResults;
    }

    const mount = this.querySelector('[ref="predictiveSearchResults"]');
    return mount instanceof HTMLElement && mount.isConnected ? mount : null;
  }

  /**
   * Keep the portaled panel aligned after morphing without re-deduping (which can drop query results).
   */
  #syncDropdownAfterMorph() {
    if (!this.#isDropdownMode || !this.#isDropdownFullyOpen()) return;

    this.#portalDropdownPanelToBody();
    this.#getActiveDropdownPanel()?.classList.add('predictive-search-dropdown-panel--open');
    this.#updateSearchDropdownPosition();
    this.#schedulePromoPanelLayout();
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getResultsContainer() {
    return this.#getResultsMount()?.querySelector('#predictive-search-results') ?? null;
  }

  /**
   * @param {HTMLElement} newResults
   * @param {'empty' | 'query'} [state]
   */
  #morphResults(newResults, state) {
    const container =
      this.#getResultsMount() ??
      this.querySelector('.predictive-search-form__content[ref="predictiveSearchResults"]') ??
      this.querySelector('.predictive-search-form__content');

    if (!(container instanceof HTMLElement)) {
      console.warn(
        '[predictive-search] No se encontró el contenedor de resultados.',
        { testId: this.dataset.testid, mode: this.dataset.searchMode, state }
      );
      return;
    }

    const cloned = /** @type {HTMLElement} */ (newResults.cloneNode(true));
    if (state) {
      cloned.dataset.searchState = state;
    }

    const existingResults = container.querySelector('#predictive-search-results');

    if (existingResults) {
      existingResults.replaceWith(cloned);
    } else {
      container.replaceChildren(cloned);
    }

    if (!this.#isDropdownMode) {
      this.#clearModalContentWrapperInlineStyles();
    }

    this.#syncDropdownAfterMorph();
    this.#syncSearchResultsLayout();

    if (state === 'query') {
      this.#updateSearchDropdownPosition({ syncPromo: false });
    } else {
      this.#schedulePromoPanelLayout();
    }
  }

  /** @returns {boolean} */
  #isShowingQueryResults() {
    return this.#getResultsContainer()?.dataset.searchState === 'query';
  }

  #ensureEmptyStateVisible() {
    if (!this.#isDropdownMode) return;
    if (this.#getSearchInput()?.value.trim().length > 0) return;
    if (this.#isSearchInFlight()) return;
    if (this.#isShowingQueryResults()) return;

    if (this.#cachedEmptyResults) {
      this.#morphResults(
        /** @type {HTMLElement} */ (this.#cachedEmptyResults.cloneNode(true)),
        'empty'
      );
      this.#schedulePromoPanelLayout();
      return;
    }

    if (this.#hasSsrEmptyState()) {
      this.#cacheDropdownEmptyState();
      this.#schedulePromoPanelLayout();
    }
  }

  #hasSsrEmptyState() {
    return !!this.#getResultsMount()?.querySelector(
      '#predictive-search-results .predictive-search-results__inner'
    );
  }

  #cacheDropdownEmptyState() {
    if (this.#getSearchInput()?.value.trim().length > 0) return;
    if (this.#isShowingQueryResults()) return;

    const results = this.#getResultsContainer();
    if (!results?.querySelector('.predictive-search-results__inner')) return;

    const cached = /** @type {HTMLElement} */ (results.cloneNode(true));
    cached.dataset.searchState = 'empty';
    this.#cachedEmptyResults = cached;
    this.#emptyStateLoaded = true;
  }

  async #fetchAndMorphEmptyState() {
    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML('predictive-search-empty', false, url);
    const emptyResultsNode = this.#parsePredictiveSearchMarkup(emptySectionMarkup);

    if (!emptyResultsNode) return;
    if (abortController.signal.aborted) return;
    if (this.#getSearchInput()?.value.trim().length > 0) return;
    if (this.#isSearchInFlight()) return;
    if (this.#isShowingQueryResults()) return;

    this.#morphResults(emptyResultsNode, 'empty');
    this.#cacheDropdownEmptyState();
    this.#resetScrollPositions();
    this.#observePromoLayoutTargets();
  }

  #closeDropdown = () => {
    if (!this.#isDropdownMode) return;
    if (!this.#shouldCloseDropdown()) return;

    this.#ignoreOutsidePointerUntil = 0;

    for (const panel of this.#getDropdownPanelCandidates()) {
      this.#clearDropdownPanelInlineStyles(panel);
      panel.classList.remove('predictive-search-form__content-wrapper--query-results');
    }

    this.#restoreDropdownPanelInForm();
    this.#dedupeDropdownPanels();

    this.#searchRequestId = 0;
    this.#searchFetch?.abort();
    this.#searchFetch = null;

    this.#searchAction?.classList.remove('is-open');
    this.classList.remove('is-open');
    document.body.classList.remove('search-dropdown-open');
    this.#setHeaderContainmentForDropdown(false);
    this.#setSearchDropdownBackdropVisible(false);
    this.#getSearchInput()?.setAttribute('aria-expanded', 'false');
    document.body.style.removeProperty('--search-dropdown-top');
    document.body.style.removeProperty('--search-dropdown-viewport-top');
    this.style.removeProperty('--search-panel-top');

    const backdrop = document.querySelector('[data-search-dropdown-backdrop]');
    if (backdrop instanceof HTMLElement) {
      backdrop.style.removeProperty('top');
    }

    this.#disconnectPromoResizeObserver();
    this.#resetHeaderSubmenuLayout();
    this.#stopScrollPositionSync();
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    const searchInput = this.#getSearchInput();
    if (!isInteractiveElement && searchInput) {
      searchInput.focus();
    }
  };

  /** Public alias for declarative on:click="/handleModalClick" in the form markup. */
  handleModalClick = (event) => {
    this.#handleModalClick(event);
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopScrollPositionSync();
    this.#restoreDropdownPanelInForm();
    this.#disconnectPromoResizeObserver();
    this.#setHeaderContainmentForDropdown(false);
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      event.preventDefault();
      if (this.#isDropdownMode) {
        this.#getSearchInput()?.focus();
        this.openDropdown();
      } else {
        this.dialog?.toggleDialog();
      }
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  /** Remove dropdown positioning accidentally applied to the mobile modal panel. */
  #clearModalContentWrapperInlineStyles() {
    if (this.#isDropdownMode) return;

    const wrapper = this.querySelector('.predictive-search-form__content-wrapper');
    if (!(wrapper instanceof HTMLElement)) return;

    wrapper.classList.remove('predictive-search-form__content-wrapper--query-results');

    wrapper.style.removeProperty('top');
    wrapper.style.removeProperty('margin-top');
    wrapper.style.removeProperty('left');
    wrapper.style.removeProperty('right');
    wrapper.style.removeProperty('width');
    wrapper.style.removeProperty('transform');
    wrapper.style.removeProperty('z-index');
    wrapper.style.removeProperty('pointer-events');
    wrapper.style.removeProperty('background-color');
  }

  #handleDialogOpen = () => {
    this.#clearModalContentWrapperInlineStyles();

    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    if (this.#getSearchInput()?.value.trim().length > 0) return;
    this.#emptyStateLoaded = true;

    if (this.#isDropdownMode) {
      this.#fetchAndMorphEmptyState();
      return;
    }

    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.#getSearchInput()?.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      if (this.#isDropdownMode) {
        this.#handleDropdownEscape(event);
      } else {
        this.#restoreEmptyState();
      }
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.#getResultsMount()?.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.#getSearchInput()?.value ?? '');
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.#getSearchInput()?.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Single debounced entry for declarative on:input and native input listener.
   * @param {Event} [event]
   */
  #runPredictiveSearchDebounced = debounce((event) => {
    if (event instanceof InputEvent && event.isComposing) return;
    this.#runPredictiveSearch();
  }, 200);

  /** Backwards-compatible handler for on:input="/search" in the form markup */
  search = (event) => {
    this.#onSearchInput(event);
  };

  #runPredictiveSearch() {
    if (this.#isDropdownMode && !this.#isDropdownFullyOpen()) {
      this.openDropdown();
    }

    const searchInput = this.#getSearchInput();
    if (!(searchInput instanceof HTMLInputElement)) return;

    const searchTerm = searchInput.value.trim();
    this.#currentIndex = -1;

    if (this.#isDropdownMode && this.#isDropdownFullyOpen()) {
      this.#updateSearchDropdownPosition();
    }

    if (!searchTerm.length) {
      this.#searchRequestId = 0;
      this.#searchFetch?.abort();
      this.#searchFetch = null;
      if (!this.#isSearchInFlight()) {
        this.#restoreEmptyState({ clearInput: false });
      }
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.#getResultsMount()?.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  /**
   * @param {string} sectionId
   * @param {URL} suggestUrl
   * @param {AbortSignal} signal
   * @returns {Promise<string>}
   */
  async #fetchPredictiveSearchSection(sectionId, suggestUrl, signal) {
    const sectionUrl = buildPredictiveSearchSectionUrl(sectionId, suggestUrl);
    const response = await fetch(sectionUrl, { signal });

    if (!response.ok) {
      throw new Error(`Predictive search request failed (${response.status})`);
    }

    return response.text();
  }

  async #getSearchResults(searchTerm) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId || !searchTerm) return;

    const requestId = ++this.#searchRequestId;
    const suggestUrl = this.#buildPredictiveSearchRequestUrl(searchTerm);

    this.#searchFetch?.abort();
    this.#searchFetch = new AbortController();
    const { signal } = this.#searchFetch;

    const resultsContainer = this.#getResultsMount();
    if (!(resultsContainer instanceof HTMLElement)) {
      console.warn('[predictive-search] Mount de resultados no disponible al buscar.', {
        testId: this.dataset.testid,
        term: searchTerm,
      });
      return;
    }

    resultsContainer.classList.add('is-search-loading');

    try {
      const resultsMarkup = await this.#fetchPredictiveSearchSection(sectionId, suggestUrl, signal);

      if (requestId !== this.#searchRequestId) return;
      if (signal.aborted) return;
      if (this.#getSearchInput()?.value.trim() !== searchTerm) return;

      const newResults = this.#parsePredictiveSearchMarkup(resultsMarkup);
      if (!newResults) {
        console.warn(
          '[predictive-search] No se pudo interpretar la respuesta de búsqueda.',
          buildPredictiveSearchSectionUrl(sectionId, suggestUrl)
        );
        return;
      }

      this.#morphResults(newResults, 'query');
      this.#resetScrollPositions();
      this.#updateSearchDropdownPosition();
      this.#observePromoLayoutTargets();
      this.#schedulePromoPanelLayout();
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('[predictive-search] Error al buscar:', error);
    } finally {
      if (requestId === this.#searchRequestId) {
        resultsContainer?.classList.remove('is-search-loading');
      }
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;
    if (!(resetButton instanceof HTMLElement)) return;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;
    if (!(resetButton instanceof HTMLElement)) return;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #restoreEmptyState = async ({ clearInput = true } = {}) => {
    this.#currentIndex = -1;
    const searchInput = this.#getSearchInput();
    if (clearInput && searchInput) {
      searchInput.value = '';
    }
    this.#hideResetButton();

    if (searchInput?.value.trim().length > 0) return;

    const abortController = this.#createAbortController();

    if (this.#isDropdownMode && this.#cachedEmptyResults) {
      if (abortController.signal.aborted) return;

      this.#morphResults(
        /** @type {HTMLElement} */ (this.#cachedEmptyResults.cloneNode(true)),
        'empty'
      );
      this.#resetScrollPositions();
      this.#updateSearchDropdownPosition();
      this.#observePromoLayoutTargets();
      this.#schedulePromoPanelLayout();
      return;
    }

    if (this.#isDropdownMode) {
      await this.#fetchAndMorphEmptyState();
      this.#updateSearchDropdownPosition();
      this.#schedulePromoPanelLayout();
      return;
    }

    await this.#resetSearch();
  };

  #resetSearch = async () => {
    const searchInput = this.#getSearchInput();
    if (!(searchInput instanceof HTMLInputElement)) return;

    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const emptyResultsNode = this.#parsePredictiveSearchMarkup(emptySectionMarkup);

    if (!emptyResultsNode) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (!this.#isDropdownMode && viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = emptyResultsNode.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    this.#morphResults(emptyResultsNode, 'empty');

    if (this.#isDropdownMode) {
      this.#cacheDropdownEmptyState();
    }

    this.#resetScrollPositions();
    this.#observePromoLayoutTargets();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
